import React, { useEffect, useState, useCallback } from 'react';
import { Icon, Spin } from 'antd';
import queryString from 'query-string';
import hpLogo from '../../images/hostplatform-logo.png';
import { getDepositConfirmationBySession } from '../../utils/apis/securityDeposit';

const fmtAmount = (amount, currency) => `${(currency || 'MYR').toUpperCase()} ${Number(amount || 0).toFixed(2)}`;

const STATUS_LABEL = {
  pending: 'Processing',
  authorized: 'On hold',
  captured: 'Charged',
  released: 'Released',
  expired: 'Expired',
  failed: 'Failed'
};

const labelStyle = { color: 'rgba(0,0,0,0.45)' };
const valueStyle = { fontWeight: 600 };

const DepositConfirmation = ({ location }) => {
  const query = queryString.parse(location.search);
  const isSuccess = query.status === 'success';
  const sessionId = query.session_id;

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(isSuccess && !!sessionId);

  const fetchDetails = useCallback(
    async (attempt = 0) => {
      try {
        const data = await getDepositConfirmationBySession(sessionId);
        setDetails(data);
        // The authorization webhook can lag the redirect — poll briefly while pending.
        if (data && data.status === 'pending' && attempt < 3) {
          setTimeout(() => fetchDetails(attempt + 1), 2500);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load deposit details:', err);
        setLoading(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (isSuccess && sessionId) {
      setLoading(true);
      fetchDetails(0);
    } else {
      setLoading(false);
    }
  }, [isSuccess, sessionId, fetchDetails]);

  const renderDetails = () => {
    if (loading) {
      return (
        <div style={{ marginTop: 16 }}>
          <Spin />
          <p style={{ ...labelStyle, marginTop: 12 }}>Confirming your hold…</p>
        </div>
      );
    }
    if (!details) {
      return (
        <p style={{ ...labelStyle, fontSize: 16 }}>
          Your card has been authorized for the security deposit. No charge has been made. You may close this page.
        </p>
      );
    }
    return (
      <div style={{ marginTop: 8, textAlign: 'left', display: 'inline-block' }}>
        <table style={{ borderSpacing: '0 6px' }}>
          <tbody>
            <tr>
              <td style={{ ...labelStyle, paddingRight: 16 }}>Amount on hold</td>
              <td style={valueStyle}>{fmtAmount(details.amount, details.currency)}</td>
            </tr>
            <tr>
              <td style={{ ...labelStyle, paddingRight: 16 }}>Reservation</td>
              <td style={valueStyle}>{details.reservationCode || '-'}</td>
            </tr>
            <tr>
              <td style={{ ...labelStyle, paddingRight: 16 }}>Status</td>
              <td style={valueStyle}>{STATUS_LABEL[details.status] || details.status || '-'}</td>
            </tr>
          </tbody>
        </table>
        <p style={{ ...labelStyle, fontSize: 13, marginTop: 16 }}>
          This is only an authorization hold — <strong>no charge has been made</strong>. It is released after check-out, or charged only if there is
          damage, per the property's terms. Once released, your bank may take up to ~10 business days to reflect it.
        </p>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f5f5f5' }}>
      <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: 480 }}>
        <img src={hpLogo} alt="HostPlatform" style={{ height: 40, marginBottom: 24 }} />
        <div>
          <Icon
            type={isSuccess ? 'check-circle' : 'close-circle'}
            theme="twoTone"
            twoToneColor={isSuccess ? '#52c41a' : '#ff4d4f'}
            style={{ fontSize: 64, marginBottom: 24 }}
          />
        </div>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>{isSuccess ? 'Security Deposit Hold Confirmed' : 'Deposit Authorization Cancelled'}</h1>
        {isSuccess ? (
          renderDetails()
        ) : (
          <p style={{ ...labelStyle, fontSize: 16 }}>The deposit authorization was cancelled. Please contact your host if you need to try again.</p>
        )}
      </div>
    </div>
  );
};

export default DepositConfirmation;
